import { LoadingState } from "@repo/app-types/client/client";
import {
  AgentTemplateKey,
  agentTemplates,
} from "@repo/app-types/entities/agentTemplate";
import { TeammateAgentKind } from "@repo/app-types/entities/teammateAgent";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { LoadingText } from "@repo/ui/Buttons/loadingText";
import { Size } from "@repo/ui/helpers/helpers";
import { Input } from "@repo/ui/Inputs/input";
import { Label } from "@repo/ui/Label/label";
import {
  IconCircleCheck,
  IconFilePlus,
  IconFileText,
  IconMessages,
  IconMoon,
} from "@tabler/icons-react";
import { useState, type FC, type ReactNode } from "react";
import { GridList, GridListItem } from "react-aria-components";
import { useNavigate } from "react-router";
import { twMerge } from "tailwind-merge";
import { useCreateTeammateAgentMutation } from "../../api/entities/teammateAgent.js";
import { appPaths } from "../../app/appPaths.js";
import { useAppDispatch } from "../../hooks/hook.js";
import {
  NotificationType,
  showNotification,
} from "../notificationsOverlay/notificationsSlice.js";

interface CreateAgentFormProps {
  close: () => void;
}

// Map template keys to icons and colors
const templateStyles: Record<
  AgentTemplateKey,
  { icon: ReactNode; iconColor: string }
> = {
  [AgentTemplateKey.Blank]: {
    icon: <IconFilePlus className="size-6 shrink-0 text-white" />,
    iconColor: "bg-gray-500",
  },
  [AgentTemplateKey.AfterHours]: {
    icon: <IconMoon className="size-6 shrink-0 text-white" />,
    iconColor: "bg-purple-600",
  },
  [AgentTemplateKey.CollectingDocuments]: {
    icon: <IconFileText className="size-6 shrink-0 text-white" />,
    iconColor: "bg-amber-600",
  },
  [AgentTemplateKey.CustomerSupport]: {
    icon: <IconMessages className="size-6 shrink-0 text-white" />,
    iconColor: "bg-success",
  },
};

export const CreateAgentForm: FC<CreateAgentFormProps> = ({ close }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [selectedTemplateKey, setSelectedTemplateKey] =
    useState<AgentTemplateKey | null>(null);

  const [createStatus, setCreateStatus] = useState<LoadingState>(
    LoadingState.LOADED,
  );

  const [createTeammateAgent] = useCreateTeammateAgentMutation();

  const onClickCreate = async () => {
    if (!name.trim() || !selectedTemplateKey) {
      return;
    }

    setCreateStatus(LoadingState.LOADING);

    try {
      const createdAgent = await createTeammateAgent({
        name: name.trim(),
        template_key: selectedTemplateKey,
        kind: TeammateAgentKind.Static,
      }).unwrap();

      showNotification(dispatch, {
        id: `agent-created-${new Date().getTime().toString()}`,
        type: NotificationType.SUCCESS,
        primaryMessage: "Agent created",
      });

      close();
      navigate(
        appPaths.teammateAgentInstructions.build({
          teammateAgentId: createdAgent.id,
        }),
      );
    } catch (error) {
      setCreateStatus(LoadingState.LOADED);
      showNotification(dispatch, {
        id: `agent-create-error-${new Date().getTime().toString()}`,
        type: NotificationType.FAILURE,
        primaryMessage: "Failed to create agent",
        secondaryMessage: "Please try again.",
      });
    }
  };

  const isFormValid = name.trim() && selectedTemplateKey;

  return (
    <div className="mt-4 flex w-[560px] flex-col space-y-6">
      <div>
        <Input
          id="name"
          label="Agent name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Lead qualification and scheduling agent"
          description="Internal reference name for this agent."
          maxLength={100}
        />
      </div>

      <div>
        <Label size={Size.SMALL} className="mb-2">
          Choose a template
        </Label>
        <p className="mb-3 text-sm text-muted-foreground">
          Select a starting point for your agent. You can customize everything
          after creation.
        </p>
        <GridList
          className="grid grid-cols-2 gap-3"
          aria-label="Agent template"
          selectionMode="single"
          selectedKeys={selectedTemplateKey ? [selectedTemplateKey] : []}
          onSelectionChange={(keys) => {
            const selected = [...keys][0] as AgentTemplateKey | undefined;
            setSelectedTemplateKey(selected ?? null);
          }}
        >
          {agentTemplates.map((template) => {
            const { icon, iconColor } = templateStyles[template.key];

            return (
              <GridListItem
                key={template.key}
                id={template.key}
                textValue={template.displayName}
                className="group relative flex cursor-pointer flex-col items-start rounded-lg border border-border-strong bg-white p-4 text-foreground shadow-xs hover:bg-gray-50 focus:outline-hidden focus-visible:border-indigo-600 focus-visible:ring-2 focus-visible:ring-indigo-600"
              >
                <div className="flex w-full justify-between">
                  <div
                    className={twMerge(
                      "mb-3 flex items-center justify-center rounded-lg p-2",
                      iconColor,
                    )}
                  >
                    {icon}
                  </div>
                  <IconCircleCheck className="invisible size-5 flex-none text-indigo-600 group-data-[selected]:visible" />
                </div>
                <span className="text-sm font-semibold">
                  {template.displayName}
                </span>
                <span className="mt-1 text-xs text-muted-foreground">
                  {template.description}
                </span>
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute -inset-px rounded-lg border-2 border-transparent group-focus-visible:border group-data-[selected]:border-indigo-600"
                />
              </GridListItem>
            );
          })}
        </GridList>
      </div>

      <div className="flex flex-row-reverse gap-3 border-t border-border-light pt-4">
        <Button
          variant={ButtonVariant.Primary}
          onClick={onClickCreate}
          isDisabled={!isFormValid || createStatus === LoadingState.LOADING}
          isLoading={createStatus === LoadingState.LOADING}
          size={Size.MEDIUM}
        >
          <LoadingText
            isLoading={createStatus === LoadingState.LOADING}
            stages={["Creating agent..."]}
          >
            Create agent
          </LoadingText>
        </Button>
        <Button
          variant={ButtonVariant.Secondary}
          onClick={close}
          size={Size.MEDIUM}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};
